import Select from 'react-select';
import { DateTimePicker } from 'react-widgets';
import moment from 'moment';
import DataTypeEditor from 'common/components/DataTypeEditor/DataTypeEditor';
import styles from './inputFields.scss';
import { verifyInputFormat, DATE_TIME} from 'common/util/dateTime';

const SingleInputField = ({
    name='',
    isAutoFocused,
    id,
    instructions,
    targetId='',
    value='',
    onValueChange,
    dataType = 'small_text',
    placeholder = 'Enter value here',
}) => {
    return (
        <div key={id} className={styles.field_container} data-cy={'SingleInputField-' + id}>
            <FieldHeader dataCy={'name-SingleInputField-' + id} name={name} instructions={instructions}/>
            <DataTypeEditor
                className={styles.textbox__input}
                placeholder={placeholder}
                autoFocus={isAutoFocused}
                dataType={dataType}
                value={value}
                changeHandler={e => onValueChange(targetId, e.target.value)}
            />
        </div>);
};

const MultiLineInputField = ({ name = '', isAutoFocused, id, instructions, targetId = '', value = '', onValueChange, placeholder = 'Enter value here' }) => {
    return (
        <div key={id} className={styles.field_container} data-cy={'div-SingleInputField-' + id}>
            <FieldHeader dataCy={'names-SingleInputField-' + id} name={name} instructions={instructions}/>
            <textarea
                type='text'
                autoFocus={isAutoFocused}
                required
                className={styles.multiline__input}
                placeholder={placeholder}
                onChange={(e) => onValueChange(targetId, e.target.value)}
                value={value || ''}
                data-cy={'text-SingleInputField-' + id}
            />
        </div>
    );
};

const DateInputField = ({ name, isAutoFocused, id, instructions, targetId, value, onValueChange }) => {
    const dateValue = moment(value).isValid() ? moment(value).toDate() : null;
    return (
        <div key={id} className={styles.field_container} data-cy={'divs-SingleInputField-' + id}>
            <FieldHeader dataCy={'names-SingleInputField-' + id} name={name} instructions={instructions}/>
            <DateTimePicker
                className={styles.dateTimePicker}
                autoFocus={isAutoFocused}
                value={dateValue}
                onChange={(e) => onValueChange(targetId, e)}
                time={false}
                format={DATE_TIME.DATE_ONLY_FORMAT}
                placeholder={DATE_TIME.DATE_ONLY_PLACEHOLDER}
            />
        </div>
    );
};

const DateTimeInputField = ({ name, isAutoFocused, id, instructions, targetId, value, onValueChange }) => {
    const dateTimeValue = moment(value, verifyInputFormat(value)).isValid() ? moment(value).toDate() : null;
    return (
        <div key={id} className={styles.field_container} data-cy={'className-SingleInputField-' + id}>
            <FieldHeader dataCy={'sName-SingleInputField-' + id} name={name} instructions={instructions}/>
            <DateTimePicker
                className={styles.dateTimePicker}
                autoFocus={isAutoFocused}
                value={dateTimeValue}
                onChange={e => onValueChange(targetId, e)}
                time={true}
                placeholder={'Select a date and time'}
            />
        </div>
    );
};

const MultiSelectInputField = ({
    name = '',
    isAutoFocused,
    id,
    instructions,
    targetId = '',
    value = [],
    dataSources = [],
    populateDropDownList,
    onValueChange,
    placeholder = 'Select value(s)'
}) => {
    const customStyles = {
        option: (provided) => ({
            ...provided,
            'color': '#0056b3',
            'font-family': 'Hind',
            'font-size': '1.143rem',
            'font-stretch': 'normal',
            'font-style': 'normal',
            'letter-spacing': 'normal'
        }),
        multiValueLabel: styles => ({
            ...styles,
            'color': '#0056b3',
            'padding': '8px',
            'font-family': 'Hind',
            'font-size': '1.143rem',
            'font-stretch': 'normal',
            'font-style': 'normal',
            'letter-spacing': 'normal'
        }),
    };
    const formatOptionsHandler = populateDropDownList || formatSelectOptions;

    return (
        <div key={id} className={styles.field_container} data-cy={'key-SingleInputField-' + id}>
            <FieldHeader dataCy={'SingleInputField-name-' + id} name={name} instructions={instructions}/>
            <Select
                styles={customStyles}
                autoFocus={isAutoFocused}
                isMulti
                onChange={(values) => onValueChange(targetId, values)}
                options={formatOptionsHandler(dataSources)}
                defaultValue={value}
                selectedOptions={value}
                placeholder={placeholder}
            />
        </div>
    );
};

const SingleSelectInputField = ({
    name = '',
    isAutoFocused,
    id,
    instructions,
    targetId = '',
    value = '',
    dataSources = [],
    populateDropDownList,
    onValueChange,
    placeholder = 'select one option',
}) => {
    const formatOptionsHandler = populateDropDownList || formatSelectOptions;
    return (
        <div key={id} className={styles.field_container} data-cy={'SingleSelectInputField-div-' + id}>
            <FieldHeader dataCy={'SingleSelectInputField-name-' + id} name={name} instructions={instructions}/>
            <Select
                autoFocus={isAutoFocused}
                options={formatOptionsHandler(dataSources)}
                defaultValue={value}
                value={value}
                placeholder={placeholder}
                isSearchable={true}
                isClearable={true}
                onChange={value => onValueChange(targetId, value)}
            />
        </div>
    );
};

const SingleNumberInputField = ({
    name = '',
    isAutoFocused,
    id,
    instructions,
    targetId = '',
    value = '',
    onValueChange,
    loading = false,
    textDisabled = false,
    placeholder = 'Enter value here',
}) => {
    return (
        <div key={id} className={styles.field_container} data-cy={'SingleNumberInputField-div-' + id}>
            <FieldHeader dataCy={'SingleNumberInputField-name-' + id} name={name} instructions={instructions}/>
            <input
                type='number'
                autoFocus={isAutoFocused}
                required
                className={styles.textbox__input}
                placeholder={placeholder}
                onChange={(e) => onValueChange(targetId, e.target.value)}
                value={value || ''}
                disabled={loading || textDisabled}
                data-cy={'SingleNumberInputField-value-' + id}
            />
        </div>
    );
};

const formatSelectOptions = (options = []) => {
    return options.map(option => {
        return {
            label: option.value,
            value: option.value,
        };
    });
};

const FieldHeader = ({ dataCy, name, instructions }) => {
    return (
        <div className={styles.header}>
            <label className={styles.helper_text} data-cy={dataCy}>{name}</label>
            <span className={styles.instructionalText}>
                { instructions }
            </span>
        </div>
    );
};

const InputFieldComponentMapping = {
    'single-value': SingleInputField,
    'multi-line': MultiLineInputField,
    'date-value': DateInputField,
    'date-time-value': DateTimeInputField,
    'multi-select': MultiSelectInputField,
    'single-select': SingleSelectInputField,
    'single-number': SingleNumberInputField,
};


const InputField = ({ inputFieldType, ...props }) => {
    const Dummy = () => <span>No Input Field Found</span>;

    const InputFieldComponent = InputFieldComponentMapping[inputFieldType] || Dummy;

    return <InputFieldComponent {...props} />;
};


export default InputField;
